import { useCallback, useEffect, useRef, useState } from "react";
import { GitCompareArrows, X } from "lucide-react";

/**
 * BeforeAfterSlider — draggable slider revealing before (source) / after
 * (edited) images side-by-side so users can instantly verify subject
 * fidelity after an edit. Falls back gracefully when either image is
 * missing.
 *
 * Props:
 *   before: string  — source image URL (pre-edit)
 *   after:  string  — edited image URL
 *   beforeLabel?: string (default "BEFORE")
 *   afterLabel?:  string (default "AFTER")
 *   showTransparencyChecker?: boolean — checkerboard tile under both images
 *                                        (useful for background-removal edits)
 *   testId?: string
 */
export function BeforeAfterSlider({
  before,
  after,
  beforeLabel = "BEFORE",
  afterLabel = "AFTER",
  showTransparencyChecker = false,
  testId = "compare-slider",
}) {
  const wrapRef = useRef(null);
  const [pos, setPos] = useState(50); // percentage 0..100
  const [dragging, setDragging] = useState(false);

  const setFromClientX = useCallback((clientX) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const move = (e) => setFromClientX(e.touches ? e.touches[0].clientX : e.clientX);
    const up   = () => setDragging(false);
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move, { passive: true });
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
  }, [dragging, setFromClientX]);

  if (!before || !after) return null;

  const checkerBg = showTransparencyChecker
    ? {
        backgroundImage:
          "linear-gradient(45deg, rgba(255,255,255,0.06) 25%, transparent 25%), linear-gradient(-45deg, rgba(255,255,255,0.06) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, rgba(255,255,255,0.06) 75%), linear-gradient(-45deg, transparent 75%, rgba(255,255,255,0.06) 75%)",
        backgroundSize: "18px 18px",
        backgroundPosition: "0 0, 0 9px, 9px -9px, -9px 0",
        backgroundColor: "#0A0A0A",
      }
    : { backgroundColor: "#0A0A0A" };

  return (
    <div
      ref={wrapRef}
      data-testid={testId}
      className="relative w-full aspect-square rounded-2xl overflow-hidden border border-[#F2C94C]/30 shadow-[0_0_28px_-10px_rgba(242,201,76,0.55)] select-none touch-none"
      style={checkerBg}
      onMouseDown={(e) => { setDragging(true); setFromClientX(e.clientX); }}
      onTouchStart={(e) => { setDragging(true); setFromClientX(e.touches[0].clientX); }}
    >
      {/* AFTER — full width base */}
      <img
        src={after}
        alt="After"
        draggable={false}
        className="absolute inset-0 w-full h-full object-contain pointer-events-none"
        data-testid={`${testId}-after-img`}
      />

      {/* BEFORE — clipped from left up to `pos` */}
      <div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{ width: `${pos}%` }}
      >
        <img
          src={before}
          alt="Before"
          draggable={false}
          className="absolute inset-0 h-full w-auto max-w-none object-contain"
          style={{ width: wrapRef.current?.offsetWidth || "auto", maxWidth: "none" }}
          data-testid={`${testId}-before-img`}
        />
      </div>

      {/* Divider */}
      <div
        aria-hidden
        className="absolute inset-y-0"
        style={{ left: `${pos}%`, transform: "translateX(-1px)" }}
      >
        <div className="w-0.5 h-full bg-gradient-to-b from-[#F2C94C] via-[#00E5FF] to-[#F2C94C] shadow-[0_0_12px_rgba(242,201,76,0.9)]" />
      </div>

      {/* Handle */}
      <button
        type="button"
        aria-label="Drag to compare"
        data-testid={`${testId}-handle`}
        onMouseDown={(e) => { e.stopPropagation(); setDragging(true); }}
        onTouchStart={(e) => { e.stopPropagation(); setDragging(true); }}
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-9 h-9 rounded-full bg-[#0A0A0A]/85 border-2 border-[#F2C94C] shadow-[0_0_18px_rgba(242,201,76,0.75)] flex items-center justify-center cursor-ew-resize hover:scale-110 transition-transform backdrop-blur"
        style={{ left: `${pos}%` }}
      >
        <GitCompareArrows className="w-4 h-4 text-[#F2C94C]" />
      </button>

      {/* Labels */}
      <div
        className="absolute top-2 left-2 font-mono-hy text-[9px] tracking-[0.24em] px-2 py-1 rounded-full bg-[#0A0A0A]/70 border border-[#F2C94C]/40 text-[#F2C94C] backdrop-blur pointer-events-none"
        data-testid={`${testId}-before-label`}
      >
        {beforeLabel}
      </div>
      <div
        className="absolute top-2 right-2 font-mono-hy text-[9px] tracking-[0.24em] px-2 py-1 rounded-full bg-[#0A0A0A]/70 border border-[#00E5FF]/40 text-[#00E5FF] backdrop-blur pointer-events-none"
        data-testid={`${testId}-after-label`}
      >
        {afterLabel}
      </div>

      {/* Percentage readout */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 font-mono-hy text-[9px] text-white tracking-widest bg-[#0A0A0A]/70 border border-white/[0.08] rounded-full px-2 py-0.5 pointer-events-none">
        {Math.round(pos)}%
      </div>
    </div>
  );
}

/**
 * BeforeAfterSliderModal — pop-out full-view compare that reuses the slider.
 */
export function BeforeAfterSliderModal({ open, onClose, before, after, showTransparencyChecker = false }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0A0A0A]/85 backdrop-blur-md p-4 sm:p-8"
      onClick={onClose}
      data-testid="compare-slider-modal"
    >
      <div
        className="relative max-w-4xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="font-mono-hy text-[10px] text-[#F2C94C] tracking-[0.24em] flex items-center gap-1.5">
            <GitCompareArrows className="w-3.5 h-3.5" /> EDIT COMPARE · DRAG TO REVEAL
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="compare-slider-modal-close"
            className="w-8 h-8 rounded-full border border-white/[0.08] hover:border-[#EB5757]/60 hover:text-[#EB5757] text-white flex items-center justify-center transition-all"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <BeforeAfterSlider
          before={before}
          after={after}
          showTransparencyChecker={showTransparencyChecker}
          testId="compare-slider-modal-slider"
        />
      </div>
    </div>
  );
}

export default BeforeAfterSlider;
